import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminHeader from '../../components/navigation/AdminHeader';
import AdminNavigation from '../../components/navigation/AdminNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import AssignmentFilters from './components/AssignmentFilters';
import AssignmentGrid from './components/AssignmentGrid';
import AssignmentDetailPanel from './components/AssignmentDetailPanel';
import BulkOperationsToolbar from './components/BulkOperationsToolbar';
import AssignmentModal from './components/AssignmentModal';
import IntegrationStatus from './components/IntegrationStatus';
import CalendarTimeline from './components/CalendarTimeline';
import { supabase } from '../../lib/supabase';

const AssignmentManagementCenter = () => {
  const navigate = useNavigate();
  const [user] = useState({ email: 'admin@rpmdiesel.com' });
  const [selectedAssignments, setSelectedAssignments] = useState([]);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [modalSaveError, setModalSaveError] = useState(null);
  const [savedPresets, setSavedPresets] = useState([
    { name: 'Active Primary', filters: { status: 'active', roleType: 'primary' } },
    { name: 'This Week', filters: { dateRange: 'this-week' } }
  ]);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showIntegrations, setShowIntegrations] = useState(false);

  // Live data from Supabase
  const [liveDrivers, setLiveDrivers] = useState([]);
  const [liveUnits, setLiveUnits] = useState([]);
  const [liveYards, setLiveYards] = useState([]);

  const [assignments, setAssignments] = useState([]);
  const [filteredAssignments, setFilteredAssignments] = useState([]);

  useEffect(() => {
    fetchDrivers();
    fetchUnits();
    fetchYards();
    fetchAssignments();
  }, []);

  const fetchDrivers = async () => {
    try {
      const { data, error } = await supabase?.from('drivers')?.select('id, first_name, last_name, is_active')?.eq('is_active', true)?.order('last_name');
      if (!error && data) {
        setLiveDrivers(data?.map(d => ({
          id: d?.id,
          name: `${d?.first_name || ''} ${d?.last_name || ''}`?.trim(),
          isActive: true
        })));
      }
    } catch (err) {
      console.error('Error fetching drivers:', err);
    }
  };

  const fetchUnits = async () => {
    try {
      const { data, error } = await supabase?.from('units')?.select('id, unit_number, make')?.order('unit_number');
      if (!error && data) {
        setLiveUnits(data?.map(u => ({
          id: u?.id,
          unitNumber: u?.unit_number,
          make: u?.make || ''
        })));
      }
    } catch (err) {
      console.error('Error fetching units:', err);
    }
  };

  const fetchYards = async () => {
    try {
      const { data, error } = await supabase?.from('yards')?.select('id, name')?.order('name');
      if (!error && data) {
        setLiveYards(data);
      }
    } catch (err) {
      console.error('Error fetching yards:', err);
    }
  };

  const fetchAssignments = async () => {
    try {
      const { data, error } = await supabase?.from('driver_units')?.select('id, driver_id, unit_id, role, start_date, end_date, is_active, drivers(first_name, last_name), units(unit_number)')?.order('id', { ascending: false });
      if (!error && data) {
        const mapped = data?.map((a, idx) => ({
          assignmentId: `ASN-${String(idx + 1)?.padStart(3, '0')}`,
          dbId: a?.id,
          assignmentType: 'driver-unit',
          driverId: String(a?.driver_id),
          driverName: a?.drivers ? `${a?.drivers?.first_name || ''} ${a?.drivers?.last_name || ''}`?.trim() : '',
          unitId: String(a?.unit_id),
          unitNumber: a?.units?.unit_number || '',
          yardLocation: '',
          roleType: a?.role || 'primary',
          startDate: a?.start_date || '',
          endDate: a?.end_date || '',
          status: a?.is_active ? 'active' : 'expired'
        }));
        setAssignments(mapped);
        setFilteredAssignments(mapped);
      }
    } catch (err) {
      console.error('Error fetching assignments:', err);
    }
  };

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e?.ctrlKey || e?.metaKey) {
        switch (e?.key) {
          case 'n':
            e?.preventDefault();
            handleCreateAssignment();
            break;
          case 'f':
            e?.preventDefault();
            document.querySelector('input[type="search"]')?.focus();
            break;
          case 'k':
            e?.preventDefault();
            setShowCalendar(!showCalendar);
            break;
          default:
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [showCalendar]);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('sessionExpiry');
    navigate('/admin-authentication-portal');
  };

  const handleFilterChange = (filters) => {
    let filtered = [...assignments];

    if (filters?.searchQuery) {
      const query = filters?.searchQuery?.toLowerCase();
      filtered = filtered?.filter(a => 
        a?.assignmentId?.toLowerCase()?.includes(query) ||
        a?.driverName?.toLowerCase()?.includes(query) ||
        a?.unitNumber?.toLowerCase()?.includes(query) ||
        a?.yardLocation?.toLowerCase()?.includes(query)
      );
    }

    if (filters?.assignmentType) {
      filtered = filtered?.filter(a => a?.assignmentType === filters?.assignmentType);
    }

    if (filters?.status) {
      filtered = filtered?.filter(a => a?.status === filters?.status);
    }

    if (filters?.roleType) {
      filtered = filtered?.filter(a => a?.roleType === filters?.roleType);
    }

    if (filters?.driverIds?.length > 0) {
      filtered = filtered?.filter(a => filters?.driverIds?.includes(a?.driverId));
    }

    if (filters?.unitIds?.length > 0) {
      filtered = filtered?.filter(a => filters?.unitIds?.includes(a?.unitId));
    }

    setFilteredAssignments(filtered);
  };

  const handleSavePreset = (name, filters) => {
    setSavedPresets([...savedPresets, { name, filters }]);
  };

  const handleSelectAssignment = (id, checked) => {
    if (checked) {
      setSelectedAssignments([...selectedAssignments, id]);
    } else {
      setSelectedAssignments(selectedAssignments?.filter(aid => aid !== id));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedAssignments(filteredAssignments?.map(a => a?.assignmentId));
    } else {
      setSelectedAssignments([]);
    }
  };

  const handleViewDetails = (assignment) => {
    setSelectedAssignment(assignment);
    setShowDetailPanel(true);
  };

  const handleCreateAssignment = () => {
    setModalMode('create');
    setEditingAssignment(null);
    setShowModal(true);
  };

  const handleEditAssignment = (assignment) => {
    setModalMode('edit');
    setEditingAssignment(assignment);
    setShowModal(true);
  };

  const handleDeleteAssignment = (assignment) => {
    if (window.confirm(`Are you sure you want to delete assignment ${assignment?.assignmentId}?`)) {
      setAssignments(assignments?.filter(a => a?.assignmentId !== assignment?.assignmentId));
      setFilteredAssignments(filteredAssignments?.filter(a => a?.assignmentId !== assignment?.assignmentId));
      if (selectedAssignment?.assignmentId === assignment?.assignmentId) {
        setSelectedAssignment(null);
        setShowDetailPanel(false);
      }
    }
  };

  const handleSaveAssignment = async (formData) => {
    setModalSaveError(null);
    try {
      if (formData?.assignmentType === 'driver-unit') {
        const dbData = {
          driver_id: formData?.driverId,
          unit_id: formData?.unitId,
          role: formData?.roleType || 'primary',
          is_active: formData?.status === 'active'
        };

        // Only include start_date if the user actually provided one
        if (formData?.startDate) {
          dbData.start_date = formData?.startDate;
        }
        // Only include end_date if provided
        if (formData?.endDate) {
          dbData.end_date = formData?.endDate;
        }

        let result;
        if (modalMode === 'edit' && editingAssignment?.dbId) {
          result = await supabase?.from('driver_units')?.update(dbData)?.eq('id', editingAssignment?.dbId);
        } else {
          result = await supabase?.from('driver_units')?.insert([dbData]);
        }

        if (result?.error) {
          const msg = result?.error?.message || 'Unknown database error';
          setModalSaveError(`Failed to ${modalMode === 'edit' ? 'update' : 'create'} assignment: ${msg}`);
          return; // Keep modal open
        }
      } else if (formData?.assignmentType === 'unit-yard') {
        const dbData = {
          unit_id: formData?.unitId,
          yard_id: formData?.yardId
        };
        if (formData?.startDate) {
          dbData.start_date = formData?.startDate;
        }
        if (formData?.endDate) {
          dbData.end_date = formData?.endDate;
        }

        let result = await supabase?.from('unit_yard_assignments')?.insert([dbData]);
        if (result?.error) {
          const msg = result?.error?.message || 'Unknown database error';
          setModalSaveError(`Failed to create assignment: ${msg}`);
          return; // Keep modal open
        }
      }

      // Success — close modal and refresh
      setShowModal(false);
      setModalSaveError(null);
      await fetchAssignments();
    } catch (err) {
      console.error('Error saving assignment:', err);
      setModalSaveError(`Failed to save assignment: ${err?.message || 'Unexpected error'}`);
    }
  };

  const handleBulkAction = (operation, value) => {
    if (operation === 'delete') {
      if (window.confirm(`Are you sure you want to delete ${selectedAssignments?.length} assignments?`)) {
        const updatedAssignments = assignments?.filter(a => !selectedAssignments?.includes(a?.assignmentId));
        setAssignments(updatedAssignments);
        setFilteredAssignments(updatedAssignments);
        setSelectedAssignments([]);
      }
    } else {
      const updatedAssignments = assignments?.map(a => {
        if (selectedAssignments?.includes(a?.assignmentId)) {
          if (operation === 'update-status') {
            return { ...a, status: value };
          } else if (operation === 'change-role') {
            return { ...a, roleType: value };
          }
        }
        return a;
      });
      setAssignments(updatedAssignments);
      setFilteredAssignments(updatedAssignments);
    }
  };

  const handleExport = () => {
    console.log('Exporting all assignments...');
  };

  return (
    <div className="min-h-screen bg-background">
      <AdminHeader user={user} onLogout={handleLogout} />
      <AdminNavigation />
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 mt-32">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg">
                <Icon name="Link" size={24} color="var(--color-primary)" strokeWidth={2} />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Assignment Management Center</h1>
                <p className="text-sm text-muted-foreground">Manage driver-unit and unit-yard assignments</p>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                iconName="Calendar"
                onClick={() => setShowCalendar(!showCalendar)}
              >
                Timeline
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="Zap"
                onClick={() => setShowIntegrations(!showIntegrations)}
              >
                Integrations
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="Download"
                onClick={handleExport}
              >
                Export
              </Button>
              <Button
                variant="default"
                iconName="Plus"
                onClick={handleCreateAssignment}
              >
                Create Assignment
              </Button>
            </div>
          </div>

          <div className="flex items-center space-x-4 text-sm text-muted-foreground">
            <div className="flex items-center space-x-2">
              <Icon name="CheckCircle2" size={16} color="var(--color-success)" strokeWidth={2} />
              <span>{assignments?.filter(a => a?.status === 'active')?.length} Active</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Clock" size={16} color="var(--color-warning)" strokeWidth={2} />
              <span>{assignments?.filter(a => a?.status === 'pending')?.length} Pending</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Calendar" size={16} color="var(--color-primary)" strokeWidth={2} />
              <span>{assignments?.filter(a => a?.status === 'upcoming')?.length} Upcoming</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="XCircle" size={16} color="var(--color-muted-foreground)" strokeWidth={2} />
              <span>{assignments?.filter(a => a?.status === 'expired')?.length} Expired</span>
            </div>
          </div>
        </div>

        {showCalendar && (
          <div className="mb-6">
            <CalendarTimeline assignments={assignments} />
          </div>
        )}

        {showIntegrations && (
          <div className="mb-6">
            <IntegrationStatus />
          </div>
        )}

        <div className="mb-6">
          <AssignmentFilters
            onFilterChange={handleFilterChange}
            onSavePreset={handleSavePreset}
            savedPresets={savedPresets}
          />
        </div>

        {selectedAssignments?.length > 0 && (
          <div className="mb-6">
            <BulkOperationsToolbar
              selectedCount={selectedAssignments?.length}
              onBulkAction={handleBulkAction}
              onClearSelection={() => setSelectedAssignments([])}
            />
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className={showDetailPanel ? 'lg:col-span-8' : 'lg:col-span-12'}>
            <AssignmentGrid
              assignments={filteredAssignments}
              selectedAssignments={selectedAssignments}
              onSelectAssignment={handleSelectAssignment}
              onSelectAll={handleSelectAll}
              onViewDetails={handleViewDetails}
              onEditAssignment={handleEditAssignment}
              onDeleteAssignment={handleDeleteAssignment}
            />
          </div>

          {showDetailPanel && (
            <div className="lg:col-span-4">
              <AssignmentDetailPanel
                assignment={selectedAssignment}
                onClose={() => {
                  setShowDetailPanel(false);
                  setSelectedAssignment(null);
                }}
                onEdit={handleEditAssignment}
              />
            </div>
          )}
        </div>

        <div className="mt-6 p-4 bg-muted/30 border border-border rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <Icon name="Keyboard" size={16} strokeWidth={2} />
              <span>Keyboard Shortcuts:</span>
              <span className="px-2 py-1 bg-card border border-border rounded text-xs font-mono">Ctrl+N</span>
              <span className="text-xs">New Assignment</span>
              <span className="px-2 py-1 bg-card border border-border rounded text-xs font-mono">Ctrl+F</span>
              <span className="text-xs">Search</span>
              <span className="px-2 py-1 bg-card border border-border rounded text-xs font-mono">Ctrl+K</span>
              <span className="text-xs">Toggle Calendar</span>
            </div>
            <span className="text-xs text-muted-foreground">
              Showing {filteredAssignments?.length} of {assignments?.length} assignments
            </span>
          </div>
        </div>
      </main>
      <AssignmentModal
        isOpen={showModal}
        onClose={() => { setShowModal(false); setModalSaveError(null); }}
        onSave={handleSaveAssignment}
        assignment={editingAssignment}
        mode={modalMode}
        drivers={liveDrivers}
        units={liveUnits}
        yards={liveYards}
        saveError={modalSaveError}
      />
    </div>
  );
};

export default AssignmentManagementCenter;